/** The catalog from the tray: what this space has not added yet, and the click that adds it. */

import type { ActivitySummary } from "@nebulize/sdk";

import { Button, EmptyState, Stack } from "@/components/ui";

import { Paged } from "../../components/Paged";
import { Muted } from "../../components/Text";
import { useAddActivity, useCatalog } from "../../spaces/lib/queries";
import type { SpaceActivities } from "../../spaces/lib/queries";
import { writeDrag } from "../lib/drop";

function matchActivity(entry: ActivitySummary, needle: string): boolean {
  return `${entry.name} ${entry.activity_id}`.toLowerCase().includes(needle);
}

export interface CatalogPanelProps {
  readonly spaceId: string;
  readonly activities: SpaceActivities;
  /** Called with what was added, so the host can select it and open where it is placed. */
  readonly onAdded: (addedActivityId: string) => void;
}

export function CatalogPanel({ spaceId, activities, onAdded }: CatalogPanelProps) {
  const catalog = useCatalog();
  const add = useAddActivity(spaceId);

  const present = activities.rows.map((one) => one.activity_id);
  const addable = (catalog.data ?? []).filter((one) => !present.includes(one.activity_id));
  const refusal = add.error?.message ?? "";

  return (
    <Stack gap={3}>
      {refusal === "" ? null : (
        <div aria-live="polite">
          <Muted small>{refusal}</Muted>
        </div>
      )}
      {catalog.isPending ? (
        <EmptyState bare headline="Reading the catalog" />
      ) : addable.length === 0 ? (
        <EmptyState bare headline="Nothing left to add" />
      ) : (
        <Paged rows={addable} pageSize={6} placeholder="Find an activity" match={matchActivity}>
          {(page) => (
            <Stack gap={3}>
              {page.map((entry) => (
                <div
                  className="cx-pick"
                  key={entry.activity_id}
                  draggable
                  onDragStart={(event) => {
                    writeDrag(event.dataTransfer, {
                      from: "catalog",
                      activityId: entry.activity_id,
                      version: entry.version,
                      name: entry.name,
                    });
                  }}
                >
                  <div className="cx-node-name">{entry.name}</div>
                  <Button
                    size="sm"
                    variant="secondary"
                    aria-label={`Add ${entry.name}`}
                    loading={add.isPending}
                    onClick={() => {
                      add.mutate(entry, {
                        onSuccess: (added) => {
                          onAdded(added.added_activity_id);
                        },
                      });
                    }}
                  >
                    Add
                  </Button>
                </div>
              ))}
            </Stack>
          )}
        </Paged>
      )}
    </Stack>
  );
}
